import {
  GridFormLayout,
  GridFormLayoutProps,
} from '@/components/GridFormLayout/GridFormLayout';
import { Dictionary } from '@/lib/get-dictionary';
import {
  AsurionSpaceValue,
  AuiContainer,
  Dropdown,
  space,
  TextField,
} from '@/lib/mx-asurion-ui-react';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { ExtractedAddress } from '../AddressSearch/types';
import FormTextField from '../FormTextField';
import { emptyAddress, stateNamesByAbbreviation } from './constants';
import {
  validateCity,
  validateStreetAddress,
  validateZipCode,
} from './helpers';

interface AddressInputProps {
  containerClassName?: string;
  onAddressCompleted: (address: ExtractedAddress) => void;
  address?: ExtractedAddress;
  disabled?: boolean;
  gridLayoutProps?: GridFormLayoutProps;
  divider?: boolean;
  dictionary?: Dictionary;
}

const AddressInput: React.FC<AddressInputProps> = ({
  containerClassName,
  onAddressCompleted = (address) => {},
  address = emptyAddress,
  disabled = false,
  gridLayoutProps,
  divider = false,
  dictionary,
}) => {
  const [address1, setAddress1] = useState(address.address1);
  const [address2, setAddress2] = useState(address.address2);
  const [city, setCity] = useState(address.city);
  const [state, setState] = useState(address.state);
  const [zip, setZip] = useState(address.zip);
  const [addressCompleted, setAddressCompleted] = useState(false);
  const [country] = useState('US');

  useEffect(() => {
    if (
      validateStreetAddress(address1) &&
      validateCity(city) &&
      state &&
      validateZipCode(zip)
    ) {
      setAddressCompleted(true);
    } else {
      setAddressCompleted(false);
    }
  }, [address1, city, state, zip]);

  useEffect(() => {
    if (addressCompleted) {
      const completeAddress = {
        address1,
        address2,
        city,
        state,
        zip,
        country,
      };
      onAddressCompleted(completeAddress);
    } else {
      onAddressCompleted(emptyAddress);
    }
  }, [
    address1,
    address2,
    city,
    addressCompleted,
    onAddressCompleted,
    state,
    zip,
    country,
  ]);

  return (
    <div
      className={clsx('address-input-root', containerClassName)}
      data-testid="address-input-root"
    >
      <AuiContainer
        gap={(gridLayoutProps?.gap as AsurionSpaceValue) ?? space[200]}
      >
        <GridFormLayout {...gridLayoutProps}>
          <FormTextField
            inputClassName="fs-mask"
            required
            label={
              dictionary?.steps['account-details'].streetAddressLabel ??
              'Street Address'
            }
            value={address1}
            disabled={disabled}
            onChange={(e) => {
              setAddress1(e.target.value);
            }}
            type="streetAddress"
            errorHelperText={
              dictionary?.steps['account-details'].streetNumberLabel ??
              'A valid home address is required.'
            }
          />
        </GridFormLayout>
        <GridFormLayout {...gridLayoutProps}>
          <TextField
            inputClassName="fs-mask"
            label={
              dictionary?.steps['account-details'].addressLine2Label ??
              'Apartment, unit, suite (optional)'
            }
            value={address2}
            disabled={disabled}
            onChange={(e) => {
              setAddress2(e.target.value);
            }}
          />
        </GridFormLayout>
        <GridFormLayout {...gridLayoutProps} columns={2}>
          <TextField
            inputClassName="fs-mask"
            label={dictionary?.steps['account-details'].cityLabel ?? 'City'}
            value={city}
            disabled={disabled}
            onChange={(e) => {
              setCity(e.target.value);
            }}
          />

          <GridFormLayout {...gridLayoutProps} columns={2}>
            <Dropdown
              containerClassName="fs-mask"
              data-testid="state-dropdown"
              label={dictionary?.steps['account-details'].stateLabel ?? 'State'}
              value={state}
              disabled={disabled}
              options={Object.keys(stateNamesByAbbreviation).map((state) => ({
                name: state,
                value: state,
              }))}
              onChange={(e) => {
                setState(e.target.value);
              }}
            />
            <TextField
              inputClassName="fs-mask"
              label={dictionary?.steps['account-details'].zipcodeLabel ?? 'Zip'}
              value={zip}
              maxLength={5}
              disabled={disabled}
              onChange={(e) => {
                setZip(e.target.value);
              }}
            />
          </GridFormLayout>
        </GridFormLayout>
      </AuiContainer>
      {divider && <hr className="border-0 border-b border-[#E6E6EB] pt-6" />}
    </div>
  );
};

export default AddressInput;
